import { format } from "date-fns";
import React, { Fragment, useEffect, useState } from "react"
import { parseJSONDateWithoutTime } from "../../utils/dateHelpers";
import getclassNames from "../../utils/getClassNames";
import Button from "../UI/Button";
import IconButton from "../UI/IconButton";
import Loader from "../UI/Loader";
import "./SearchResultsTable.css";
import { createFormInstance, getClientFormInstance, getLatestForm } from "../../services/formsService";
import FormViewer from "../UI/FormViewer";
import Client from "../../models/serviceModels/Client";
import HouseholdMember from "../../models/serviceModels/HouseholdMember";
import { getClientById } from "../../services/clientsService";
import { Link } from "react-router-dom";
import { updateClientResultsEnabled } from "../../utils/features";
import Dropdown from "../UI/Dropdown";
import FormEditor from "../Form/Editor/FormEditor";
import Form from "../../models/serviceModels/Form";
import EditableFormField from "../../models/pageModels/EditableFormField";
import { mapFormFieldToEditableFormField } from "../../mappers/formFieldMapper";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import { UPLOADED } from "../../constants/fileUploadStatus";
import toast from "react-hot-toast";

interface SearchResultsTableProps {
    clients: Client[];
}

interface SelectedForm {
    client: Client;
    formId: string;
    formName: string;
}

const SearchResultsTable = (props: SearchResultsTableProps) => {
    const [expandedClients, setExpandedClients] = useState<Client[]>([]);
    const [loadingClientForms, setLoadingClientForms] = useState<any[]>([]);
    const [loadingClients, setLoadingClients] = useState<string[]>([]);
    const [clientForms, setClientForms] = useState<any[]>([]);
    const [loadedClients, setLoadedClients] = useState<Client[]>([]);
    const [selectedForm, setSelectedForm] = useState<SelectedForm>();
    const [loadingSelectedForm, setLoadingSelectedForm] = useState(false);
    const [loadedForm, setLoadedForm] = useState<Form>();
    const [loadedFormFields, setLoadedFormFields] = useState<EditableFormField[]>([]);
    const [apiError, setApiError] = useState<any>();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        closeForm();
    }, [props.clients]);

    const handleOnExpand = (client: Client) => {
        setExpandedClients([
            ...expandedClients,
            client
        ]);

        const loadedClient = loadedClients.find(c => c.id === client.id);
        if (!loadedClient) {
            handleLoadingClient(client);
        }

        const existingForm = clientForms.find(cf => cf.clientId === client.id);
        if (!existingForm) {
            handleLoadingClientForm(client);
        }
    };

    const handleOnCollapse = (client: Client) => {
        setExpandedClients([
            ...expandedClients.filter(c => c !== client)
        ])
    }

    const handleLoadingClient = (client: Client) => {
        setLoadingClients([
            ...loadingClients,
            client.id
        ]);

        getClientById(client.id).then(c => {
            setLoadedClients([
                ...loadedClients,
                c
            ])
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            setLoadingClients([
                ...loadingClients.filter(lc => lc !== client.id)
            ]);
        })
    };

    const openForm = (form: SelectedForm) => {
        setSelectedForm(form);
        setLoadingSelectedForm(true);

        getLatestForm(form.formId, true).then(f => {
            setLoadedForm(f);
            setLoadedFormFields(f.fields.map(ff => mapFormFieldToEditableFormField(ff)));
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoadingSelectedForm(false);
        });
    };

    const closeForm = () => {
        setSelectedForm(undefined);
        setApiError(undefined);
        setLoadingSelectedForm(false);
        setLoadedForm(undefined);
        setLoadedFormFields([]);
    };

    const handleLoadingClientForm = (client: Client) => {
        setLoadingClientForms([
            ...loadingClientForms,
            client
        ]);

        getClientFormInstance(client.id).then(formInstance => {
            if (formInstance) {
                setClientForms([
                    ...clientForms,
                    {
                        clientId: client.id,
                        ...formInstance
                    }
                ]);
            }
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            setLoadingClientForms([
                ...loadingClientForms.filter(lcf => lcf !== client)
            ]);
        });
    }

    const getClientForm = (clientId: string) => {
        return clientForms.find(cf => cf.clientId === clientId);
    };

    const getLoadedClient = (clientId: string) => {
        return loadedClients.find(lc => lc.id === clientId);
    };

    const handleSubmitForm = (e: React.FormEvent) => {
        e.preventDefault();
        setApiError(undefined);

        if (!loadedForm?.id || !selectedForm) {
            console.warn('No loaded form or selected form');
            return;
        }

        setSubmitting(true);

        createFormInstance({
            formId: loadedForm.id,
            versionId: loadedForm.versionId,
            clientId: selectedForm.client.id,
            fields: loadedFormFields.map(f => {
                return {
                    fieldId: f.id,
                    value: f.value,
                    files: f.files?.map(f => f.fileId!) || []
                }
            })
        }).then(() => {
            toast.success("Successfully submitted form");
            closeForm();
        }).catch(e => {
            setApiError(e);
            console.error(e);
            toast.error("Failed to submit form");
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const handleOnFieldsChange = (fields: EditableFormField[]) => {
        setLoadedFormFields([
            ...fields
        ]);
    };

    const handleFormReset = () => {
        setLoadedFormFields([
            ...loadedFormFields.map(f => {
                return {
                    ...f,
                    value: '',
                    files: []
                }
            })
        ])
    };

    const getRecentForms = (client: Client) => {
        const options = [];
        if (client.recentFormId1 && client.recentFormName1) {
            options.push({
                label: client.recentFormName1,
                onClick: () => openForm({
                    formId: client.recentFormId1!,
                    formName: client.recentFormName1!,
                    client: client
                })
            });
        }

        if (client.recentFormId2 && client.recentFormName2) {
            options.push({
                label: client.recentFormName2,
                onClick: () => openForm({
                    formId: client.recentFormId2!,
                    formName: client.recentFormName2!,
                    client: client
                })
            });
        }

        if (client.recentFormId3 && client.recentFormName3) {
            options.push({
                label: client.recentFormName3,
                onClick: () => openForm({
                    formId: client.recentFormId3!,
                    formName: client.recentFormName3!,
                    client: client
                })
            });
        }

        return options;
    };

    return (
        <div className='search-results-container'>
            <hr />
            {props.clients &&
                <div className="row justify-content-center">
                    <div className={selectedForm ? "column-medium-12" : "column-medium-8"}>
                        <caption>
                            <h2 className="text-center">Participant Search Results</h2>
                        </caption>
                        <div className="row">
                            <div className="column">
                                <table className="table">
                                    <thead>
                                        {updateClientResultsEnabled && <tr>
                                            <th></th>
                                            <th scope="col">name</th>
                                            <th scope="col">birthdate</th>
                                            <th scope="col">phone number</th>
                                            <th scope="col">updated</th>
                                            <th scope="col">track data</th>
                                        </tr>}
                                        {!updateClientResultsEnabled && <tr>
                                            <th></th>
                                            <th scope="col">last name</th>
                                            <th scope="col">first name</th>
                                            <th scope="col">birthdate</th>
                                            <th></th>
                                        </tr>}
                                    </thead>
                                    <tbody>
                                        {props.clients.map((c: Client, i: number) => (
                                            <Fragment key={`client-result-${c.id}`}>
                                                <tr className={getclassNames('table-row', (i % 2 === 0 && 'even-row') || '')}>
                                                    <td>
                                                        {!expandedClients.includes(c) && <IconButton className="small flat" id={`expand-line=${i}`} icon={<i className="ri-arrow-down-s-line"></i>} onClick={() => handleOnExpand(c)} />}
                                                        {expandedClients.includes(c) && <IconButton className="small flat" id={`expand-line=${i}`} icon={<i className="ri-arrow-up-s-line"></i>} onClick={() => handleOnCollapse(c)} />}
                                                    </td>
                                                    {updateClientResultsEnabled && <td><Link to={`/participants/${c.id}/profile-demographics`} target="_blank">{c.lastName}, {c.firstName}</Link></td>}
                                                    {!updateClientResultsEnabled && <>
                                                        <td>{c.lastName}</td>
                                                        <td>{c.firstName}</td>
                                                    </>}
                                                    <td>{c.dateOfBirth && format(parseJSONDateWithoutTime(c.dateOfBirth), 'MM/dd/yyyy')}</td>
                                                    {updateClientResultsEnabled && <>
                                                        <td>{c.phoneNumber}</td>
                                                        <td>{(c.updated && format(new Date(c.updated), 'Pp')) || (c.created && format(new Date(c.created), 'Pp'))} </td>
                                                        <td>
                                                            {(c.recentFormId1 || c.recentFormId2 || c.recentFormId3) && <Dropdown id={`recent-forms-${c.id}`}
                                                                options={getRecentForms(c)}
                                                                optionSize="large" />}
                                                        </td>
                                                    </>}
                                                    {!updateClientResultsEnabled && <td>
                                                        <Button className="small margin-start-2"
                                                            href={`/participants/${c.id}/profile-demographics`}
                                                            id={`view-client-${i}`}
                                                            text="Profile"
                                                            icon={<i className="ri-arrow-right-s-line" />}
                                                            iconLocation='end' />
                                                    </td>}
                                                </tr>
                                                {expandedClients.includes(c) && (
                                                    <tr className="table-details-row">
                                                        <td colSpan={7}>
                                                            <div className="row padding-3">
                                                                <div className="column">
                                                                    {loadingClients.includes(c.id) && <Loader />}
                                                                    {!loadingClients.includes(c.id) && getLoadedClient(c.id) && <>
                                                                        <div className="row margin-top-2">
                                                                            <div className="column-medium-3">
                                                                                <label>Name</label>
                                                                                <div>{getLoadedClient(c.id)!.firstName} {getLoadedClient(c.id)!.middleName} {getLoadedClient(c.id)!.lastName}</div>
                                                                            </div>
                                                                            <div className="column-medium-3">
                                                                                <label>Date of Birth</label>
                                                                                <div>{getLoadedClient(c.id)!.dateOfBirth && format(parseJSONDateWithoutTime(getLoadedClient(c.id)!.dateOfBirth), 'MM/dd/yyyy')}</div>
                                                                            </div>
                                                                            <div className="column-medium-3">
                                                                                <label>Gender</label>
                                                                                <div>{getLoadedClient(c.id)!.genderName}</div>
                                                                            </div>
                                                                            <div className="column-medium-3">
                                                                                <label>Ethnicity</label>
                                                                                <div>{getLoadedClient(c.id)!.ethnicityName}</div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="row margin-top-2">
                                                                            <div className="column">
                                                                                <label>Race(s)</label>
                                                                                <div>{getLoadedClient(c.id)!.races.map(r => r.name).join(', ')}</div>
                                                                            </div>
                                                                        </div>

                                                                        {loadingClientForms.includes(c) && <div className="row justify-content-center margin-top-2">
                                                                            <div className="column-auto">
                                                                                <Loader />
                                                                            </div>
                                                                        </div>}
                                                                        {!loadingClientForms.includes(c) && clientForms.some(cf => cf.clientId === c.id) && (
                                                                            <FormViewer instance={getClientForm(c.id)} />
                                                                        )}
                                                                        {getLoadedClient(c.id)!.householdMembers && getLoadedClient(c.id)!.householdMembers.length > 0 && <h3>Household Members</h3>}
                                                                        {getLoadedClient(c.id)!.householdMembers && getLoadedClient(c.id)!.householdMembers.length > 0 && getLoadedClient(c.id)!.householdMembers.map((hhm: HouseholdMember) => {
                                                                            return (<Fragment key={`client-household-member-${hhm.id}`}>
                                                                                <hr />
                                                                                <div className="row">
                                                                                    <div className="column">
                                                                                        <div className="row">
                                                                                            <div className="column-medium-3">
                                                                                                <label>Name</label>
                                                                                                <div>{hhm.firstName} {hhm.middleName} {hhm.lastName}</div>
                                                                                            </div>
                                                                                            <div className="column-medium-3">
                                                                                                <label>Date of Birth</label>
                                                                                                <div>{format(parseJSONDateWithoutTime(hhm.dateOfBirth), 'MM/dd/yyyy')}</div>
                                                                                            </div>
                                                                                            <div className="column-medium-3">
                                                                                                <label>Gender</label>
                                                                                                <div>{hhm.genderName}</div>
                                                                                            </div>
                                                                                            <div className="column-medium-3">
                                                                                                <label>Ethnicity</label>
                                                                                                <div>{hhm.ethnicityName}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="row">
                                                                                            <div className="column">
                                                                                                <label>Race(s)</label>
                                                                                                <div>{hhm.races.map(r => r.name).join(', ')}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </Fragment>)
                                                                        })}
                                                                    </>}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {selectedForm && <>
                                {loadingSelectedForm && <Loader />}
                                {!loadingSelectedForm && loadedForm && <div className="column border-start padding-start-5">
                                    <div className="row justify-content-between">
                                        <div className="column-auto">
                                            <h3>{selectedForm.formName}</h3>
                                            <h4>{selectedForm.client.lastName}, {selectedForm.client.firstName}</h4>
                                        </div>
                                        <div className="column-auto">
                                            <IconButton id="close-form-button" 
                                                icon={<i className="ri-close-line"></i>} 
                                                className="flat small" onClick={closeForm} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="column">
                                            <form onSubmit={handleSubmitForm}>
                                                <ApiErrorMessages error={apiError} />
                                                <div className="column">
                                                    <FormEditor disabled={false}
                                                        fields={loadedFormFields}
                                                        onChange={handleOnFieldsChange}
                                                        columnNumber={2} />
                                                </div>
                                                <div className="row margin-top-3 justify-content-between">
                                                    <div className="column-auto">
                                                        <Button id="reset-button"
                                                            className="secondary"
                                                            text="Reset"
                                                            disabled={submitting}
                                                            onClick={handleFormReset} />
                                                    </div>
                                                    <div className="column-auto">
                                                        <Button id="submit-button"
                                                            text="Submit"
                                                            disabled={submitting || loadedFormFields.some(f => f.files && f.files.some(ff => ff.status !== UPLOADED))}
                                                            loading={submitting}
                                                            type="submit" />
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>}
                            </>}
                        </div>
                    </div>
                </div>
            }
            {
                props.clients && props.clients.length === 0 && <div className="row justify-content-center">
                    <div className="column-auto">
                        <p>No Matches For Your Search</p>
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <Button id="create-participant" href="/participants/add" text="Add Participant" />
                            </div>
                        </div>

                    </div>
                </div>
            }
        </div >
    )
}
export default SearchResultsTable;